import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { canAccess } from '../utils/canAccess';
import { Form, Button, Alert, Card, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import { utils, writeFile } from 'xlsx';
import {
  FaCalendarAlt,
  FaSearch,
  FaHardHat,
  FaUsers,
  FaMoneyBillWave,
  FaEye
} from 'react-icons/fa';
import { format, parseISO } from 'date-fns';
import fr from 'date-fns/locale/fr';

const RapportChantierPage = ({ user }) => {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [chantierOptions, setChantierOptions] = useState([]);
  const [selectedChantiers, setSelectedChantiers] = useState([]);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [details, setDetails] = useState([]);
  const [selectedChantierName, setSelectedChantierName] = useState('');
  const [expandedCards, setExpandedCards] = useState({});
  const toggleExpand = (idx) => setExpandedCards(prev => ({ ...prev, [idx]: !prev[idx] }));
  const exportToExcel = () => {
    const grouped = groupByDateThenOuvrier(selectedChantierName);
    const ouvriers = Array.from(
      new Set(Object.values(grouped).flatMap(mois => Object.keys(mois)))
    );
  
    const rows = [];
  
    Object.entries(grouped).forEach(([mois, values]) => {
      const row = { Mois: mois, "Total estimé": 0 };
      let total = 0;
  
      ouvriers.forEach(o => {
        const d = values[o];
        if (d) {
          const paie = (d.jours + d.heures / 10) * d.salaire;
          row[o] = `${d.jours}j / ${d.heures}h`;
          total += paie;
        } else {
          row[o] = '—';
        }
      });
  
      row["Total estimé"] = total;
      rows.push(row);
    });
  
    const ws = utils.json_to_sheet(rows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, selectedChantierName);
    writeFile(wb, `rapport_${selectedChantierName}.xlsx`);
  };
  // 🔁 Règle auto date : 21 → 20
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    let startDate, endDate;
    if (today.getDate() < 21) {
      startDate = new Date(year, month - 1, 21);
      endDate = new Date(year, month, 20);
    } else {
      startDate = new Date(year, month, 21);
      endDate = new Date(year, month + 1, 20);
    }

    setStart(startDate.toISOString().split('T')[0]);
    setEnd(endDate.toISOString().split('T')[0]);
  }, []);

  const handleFetch = async () => {
    if (!start || !end) {
      return setError("📅 Merci de choisir une date de début et de fin");
    }

    try {
      const res = await axios.get(`/pointages?start=${start}&end=${end}`);
      const chantierStats = await axios.get(`/rapport/stats?start=${start}&end=${end}`);
      const sorted = [...chantierStats.data].sort((a, b) => b.totalPaie - a.totalPaie);
      setData(sorted);
      setFilteredData(sorted);
      const uniqueChantiers = chantierStats.data.map(d => ({
        label: d.chantier,
        value: d.chantier
      }));
      setChantierOptions(uniqueChantiers);
      setDetails(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur chargement rapport 😓');
    }
  };

  const handleFilterChange = selected => {
    setSelectedChantiers(selected);
    if (!selected || selected.length === 0) {
      setFilteredData(data);
    } else {
      const selectedValues = selected.map(opt => opt.value);
      setFilteredData(data.filter(item => selectedValues.includes(item.chantier)).sort((a, b) => b.totalPaie - a.totalPaie));
    }
  };

  const formatSalaire = montant =>
    Math.round(montant).toLocaleString('fr-FR').replace(/,/g, ' ');

  const groupByDateThenOuvrier = chantierName => {
    const chantierPointages = details.filter(p => p.chantier === chantierName);
    const grouped = {};

    chantierPointages.forEach(p => {
      const mois = format(parseISO(p.date), 'MMMM yyyy', { locale: fr });
      const ouvrier = `${p.ouvrier.name} (${p.ouvrier.cin})`;
      const salaire = p.ouvrier.salary || 0;

      if (!grouped[mois]) grouped[mois] = {};
      if (!grouped[mois][ouvrier]) {
        grouped[mois][ouvrier] = { jours: 0, heures: 0, salaire };
      }

      grouped[mois][ouvrier].jours += p.jours;
      grouped[mois][ouvrier].heures += p.heures;
    });

    return grouped;
  };

  const handleShowDetails = chantier => {
    setSelectedChantierName(chantier);
    setShowModal(true);
  };

  if (!canAccess(user, 'rapport-chantier')) {
    return <Alert variant="danger" className="m-4">⛔ Accès non autorisé</Alert>;
  }
  

  return (
    <div className="container mt-4 mb-5">
      <h3 className="mb-4">📊 Rapport par Chantier (vue cartes)</h3>

      {/* 🎛️ Filtres */}
      <div className="row mb-3">
        <div className="col-md-3">
          <Form.Label><FaCalendarAlt className="me-2" />Date de début</Form.Label>
          <Form.Control type="date" value={start} onChange={e => setStart(e.target.value)} />
        </div>
        <div className="col-md-3">
          <Form.Label><FaCalendarAlt className="me-2" />Date de fin</Form.Label>
          <Form.Control type="date" value={end} onChange={e => setEnd(e.target.value)} />
        </div>
        <div className="col-md-4">
          <Form.Label><FaHardHat className="me-2" />Filtrer par chantier</Form.Label>
          <Select
            isMulti
            isSearchable
            placeholder="🏗️ Sélectionner un ou plusieurs chantiers..."
            options={chantierOptions}
            value={selectedChantiers}
            onChange={handleFilterChange}
          />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <Button variant="info" className="w-100" onClick={handleFetch}>
            <FaSearch className="me-2" />Générer
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* 💳 Cartes */}
      <div className="row mt-4">
        {filteredData.length > 0 ? (
          filteredData.map((item, idx) => (
            <div className="col-md-4 mb-4" key={idx}>
              <Card
  className="h-100 card-rapport"
  style={{
    border: `2px solid ${['#8b5e3c', '#6d4e36', '#e6b87b'][idx % 3]}`,
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease-in-out',
    backgroundColor: '#fdfcf7'
  }}
  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.02)')}
  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
>
  <Card.Body className="text-center d-flex flex-column justify-content-between gap-2">
    <Card.Title className="fw-bold text-dark mb-3">
      <FaHardHat className="me-2 text-warning" />
      {item.chantier}
    </Card.Title>
    <div>
      <Card.Text className="mb-1">
        <FaUsers className="me-2 text-primary" />
        <strong>{item.nbOuvriers}</strong> ouvrier(s)
      </Card.Text>
      {item.parQualification?.length > 0 && (
        <div className="mb-2" style={{ fontSize: '0.78rem', color: '#555' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
            {(expandedCards[idx] ? item.parQualification : item.parQualification.slice(0, 5)).map((q, i) => (
              <span key={i} style={{
                background: '#f0ebe5',
                border: '1px solid #c8b49a',
                borderRadius: 10,
                padding: '1px 8px'
              }}>
                {q.qualif} <strong>{q.nb}</strong>
              </span>
            ))}
          </div>
          {item.parQualification.length > 5 && (
            <button
              onClick={() => toggleExpand(idx)}
              style={{ background: 'none', border: 'none', color: '#8b5e3c', cursor: 'pointer', fontSize: '0.75rem', marginTop: 3, width: '100%' }}
            >
              {expandedCards[idx] ? '▲ Moins' : `▼ +${item.parQualification.length - 5} autres`}
            </button>
          )}
        </div>
      )}
      <Card.Text className="mb-2">
        <FaMoneyBillWave className="me-2 text-success" />
        <strong>{formatSalaire(item.totalPaie)} DH</strong>
      </Card.Text>
    </div>
    <Button
      variant="outline-dark"
      size="sm"
      onClick={() => handleShowDetails(item.chantier)}
      style={{ borderRadius: '8px', fontWeight: 'bold' }}
    >
      <FaEye className="me-1" /> Détails
    </Button>
  </Card.Body>
</Card>
            </div>
          ))
        ) : (
          <p className="text-muted text-center">Aucun chantier à afficher. Lance une recherche 🔍</p>
        )}
      </div>

      {/* 🔍 Modale Détail : Dates → Ouvriers */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>📅 Détail par mois – {selectedChantierName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const grouped = groupByDateThenOuvrier(selectedChantierName);
            const ouvriers = Array.from(
              new Set(
                Object.values(grouped).flatMap(mois => Object.keys(mois))
              )
            );

            return (
              <Table bordered hover responsive size="sm">
                <thead className="table-light text-center">
                  <tr>
                    <th>Mois </th>
                    <th>Total estimé 💰</th>
                    {ouvriers.map((o, i) => (
                      <th key={i}>{o}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(grouped).map(([mois, values], i) => {
                    let total = 0;
                    const rowCells = ouvriers.map((o, j) => {
                      const d = values[o];
                      if (d) {
                        const paie = (d.jours + d.heures / 10) * d.salaire;
                        total += paie;
                        return <td key={j}>{d.jours}j / {d.heures}h</td>;
                      }
                      return <td key={j}>—</td>;
                    });

                    return (
                      <tr key={i}>
                        <td className="fw-bold">{mois}</td>
                        <td className="fw-bold text-success">{formatSalaire(total)} DH</td>
                        {rowCells}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
  <Button variant="outline-success" onClick={exportToExcel}>
    📊 Export Excel
  </Button>
  <Button variant="secondary" onClick={() => setShowModal(false)}>
    Fermer
  </Button>
</Modal.Footer>

      </Modal>
    </div>
  );
};

export default RapportChantierPage;
